import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Edit, Users, Mail, UserPlus, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import * as Sentry from '@sentry/react';
import { UserRoleChangeModal } from "@/components/UserRoleChangeModal";

interface OrganizationUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: 'admin' | 'user';
  created_at: string;
}

const Organizations: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast: toastHook } = useToast();
  const queryClient = useQueryClient();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState<{ id: string; name: string; logo_url?: string; created_at: string } | null>(null);
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgLogo, setNewOrgLogo] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [roleChangeUser, setRoleChangeUser] = useState<OrganizationUser | null>(null);

  // Redirect if not admin
  React.useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      toast.error("Access denied. Admin privileges required.");
    }
  }, [user, navigate]);

  const { data: organizations, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: user?.role === 'admin',
  });

  // Fetch users for the editing organization
  const { data: organizationUsers, isLoading: usersLoading } = useQuery({
    queryKey: ['organization-users', editingOrg?.id],
    queryFn: async () => {
      if (!editingOrg?.id) return [];

      const { data, error } = await supabase
        .from('user_organizations')
        .select(`
          users (
            id,
            email,
            first_name,
            last_name,
            role,
            created_at
          )
        `)
        .eq('organization_id', editingOrg.id);

      if (error) throw error;
      return data?.map(item => item.users).filter(Boolean) as OrganizationUser[];
    },
    enabled: !!editingOrg?.id,
  });

  const createMutation = useMutation({
    mutationFn: async ({ name, logo_url }: { name: string; logo_url?: string }) => {
      const { data, error } = await supabase
        .from('organizations')
        .insert([{ name, logo_url }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setIsCreateDialogOpen(false);
      setNewOrgName("");
      setNewOrgLogo("");
      toast.success("Organization created successfully");
    },
    onError: (error) => {
      toast.error("Failed to create organization");
                Sentry.captureException(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, name, logo_url }: { id: string; name: string; logo_url?: string }) => {
      const { data, error } = await supabase
        .from('organizations')
        .update({ name, logo_url })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setEditingOrg(null);
      toast.success("Organization updated successfully");
    },
    onError: (error) => {
      toast.error("Failed to update organization");
                Sentry.captureException(error);
    },
  });

  const inviteUserMutation = useMutation({
    mutationFn: async ({ email, organizationId }: { email: string; organizationId: string }) => {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: email.toLowerCase(),
          organizationId
        }
      });

      if (error) {
                  Sentry.captureException(error);
        throw new Error(error.message || 'Failed to invite user');
      }

      if (!data) {
        throw new Error('No response from invite function');
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organization-users', editingOrg?.id] });
      setInviteEmail("");
      const orgName = data?.organizationName || editingOrg?.name || 'organization';
      toast.success(`User invited successfully to ${orgName}`);
    },
    onError: (error: any) => {
                Sentry.captureException(error);
      toast.error(error.message || "Failed to invite user");
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: async ({ userId, organizationId }: { userId: string; organizationId: string }) => {
      console.log(`Attempting to remove user ${userId} from organization ${organizationId}`);

      const { error } = await supabase
        .from('user_organizations')
        .delete()
        .eq('user_id', userId)
        .eq('organization_id', organizationId);

      if (error) {
                  Sentry.captureException(error);
        throw error;
      }

      console.log('User removed successfully from database');
    },
    onSuccess: (_, variables) => {
      console.log('Remove user mutation succeeded, invalidating queries');
      queryClient.invalidateQueries({ queryKey: ['organization-users', editingOrg?.id] });
      toast.success("User removed from organization");
    },
    onError: (error) => {
                Sentry.captureException(error);
      toast.error(`Failed to remove user: ${error.message}`);
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async ({ email, organizationId }: { email: string; organizationId: string }) => {
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          email: email.toLowerCase(),
          organizationId
        }
      });

      if (error) {
                  Sentry.captureException(error);
        throw new Error(error.message || 'Failed to resend invite');
      }

      if (!data) {
        throw new Error('No response from invite function');
      }

      return data;
    },
    onSuccess: (data) => {
      console.log('Resend invite response:', data);
      toast.success(`Invite resent successfully to ${data?.organizationName || editingOrg?.name || 'organization'}`);
    },
    onError: (error: any) => {
                  Sentry.captureException(error);
      toast.error(error.message || "Failed to resend invite");
    },
  });

  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: 'admin' | 'user' }) => {
      const { data, error } = await supabase.functions.invoke('update-user-role', {
        body: { userId, newRole }
      });

      if (error) {
        Sentry.captureException(error);
        throw new Error(error.message || 'Failed to update user role');
      }

      if (!data?.success) {
        throw new Error('Failed to update user role');
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-users', editingOrg?.id] });
      setRoleChangeUser(null);
      toast.success("User role updated successfully");
    },
    onError: (error: any) => {
      Sentry.captureException(error);
      toast.error(error.message || "Failed to update user role");
    },
  });

  const handleCreate = () => {
    if (!newOrgName.trim()) {
      toast.error("Organization name is required");
      return;
    }
    createMutation.mutate({ name: newOrgName.trim(), logo_url: newOrgLogo.trim() || undefined });
  };

  const handleUpdate = () => {
    if (!editingOrg?.name?.trim()) {
      toast.error("Organization name is required");
      return;
    }
    updateMutation.mutate({
      id: editingOrg.id,
      name: editingOrg.name.trim(),
      logo_url: editingOrg.logo_url?.trim() || undefined
    });
  };

  const handleInviteUser = () => {
    if (!inviteEmail.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!editingOrg?.id) {
      toast.error("No organization selected");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }

    inviteUserMutation.mutate({
      email: inviteEmail.trim(),
      organizationId: editingOrg.id
    });
  };

  const handleRemoveUser = (userId: string) => {
    if (!editingOrg?.id) {
      toast.error("No organization selected");
      return;
    }

    console.log(`Remove user button clicked for user ${userId} from org ${editingOrg.id}`);

    removeUserMutation.mutate({
      userId,
      organizationId: editingOrg.id
    });
  };

  const handleCopyInvite = (userEmail: string) => {
    const inviteUrl = `${window.location.origin}/invited?email=${encodeURIComponent(userEmail)}`;
    navigator.clipboard.writeText(inviteUrl).then(() => {
      toastHook({
        title: "Invite link copied!",
        description: "The invite link has been copied to your clipboard.",
      });
    }).catch(() => {
      // Fallback: navigate to the invite page
      navigate(`/invited?email=${encodeURIComponent(userEmail)}`);
    });
  };

  const formatUserName = (user: OrganizationUser) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.email;
  };

  const handleRoleChange = () => {
    if (!roleChangeUser) return;
    
    const newRole = roleChangeUser.role === 'admin' ? 'user' : 'admin';
    updateUserRoleMutation.mutate({
      userId: roleChangeUser.id,
      newRole
    });
  };

  if (user?.role !== 'admin') {
    return null;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Organization Management</h1>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Organization Management</h1>
          <p className="text-muted-foreground">
            Manage organizations in the system
          </p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Organization
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Organization</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Organization Name</Label>
                <Input
                  id="name"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="Enter organization name..."
                />
              </div>
              <div>
                <Label htmlFor="logo">Logo URL (optional)</Label>
                <Input
                  id="logo"
                  value={newOrgLogo}
                  onChange={(e) => setNewOrgLogo(e.target.value)}
                  placeholder="Enter logo URL..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Creating..." : "Create"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {organizations?.map((org) => (
          <Card key={org.id}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                {org.logo_url ? (
                  <img src={org.logo_url} alt={org.name} className="h-6 w-6 rounded" />
                ) : (
                  <Building2 className="h-4 w-4" />
                )}
                {org.name}
              </CardTitle>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingOrg(org)}
                >
                  <Edit className="h-3 w-3" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-muted-foreground">
                Created: {new Date(org.created_at).toLocaleDateString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingOrg} onOpenChange={() => setEditingOrg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Organization</DialogTitle>
          </DialogHeader>
          {editingOrg && (
            <div className="space-y-6">
              {/* Organization Details Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Organization Details</h3>
                <div>
                  <Label htmlFor="edit-name">Organization Name</Label>
                  <Input
                    id="edit-name"
                    value={editingOrg.name}
                    onChange={(e) => setEditingOrg({ ...editingOrg, name: e.target.value })}
                    placeholder="Enter organization name..."
                  />
                </div>
                <div>
                  <Label htmlFor="edit-logo">Logo URL (optional)</Label>
                  <Input
                    id="edit-logo"
                    value={editingOrg.logo_url || ""}
                    onChange={(e) => setEditingOrg({ ...editingOrg, logo_url: e.target.value })}
                    placeholder="Enter logo URL..."
                  />
                </div>
              </div>

              <Separator />

              {/* Users Section */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  <h3 className="text-lg font-medium">Organization Users</h3>
                </div>

                {/* User List */}
                {usersLoading ? (
                  <div className="text-sm text-muted-foreground">Loading users...</div>
                ) : organizationUsers?.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No users in this organization</div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {organizationUsers?.map((orgUser) => (
                      <div
                        key={orgUser.id}
                        className="flex items-center justify-between p-3 border rounded-md"
                      >
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-glyco-purple-light flex items-center justify-center">
                            <span className="text-sm font-medium text-glyco-purple">
                              {formatUserName(orgUser).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium">{formatUserName(orgUser)}</p>
                            <p className="text-xs text-muted-foreground">{orgUser.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={orgUser.role === 'admin' ? 'default' : 'secondary'}
                            className="cursor-pointer hover:opacity-80"
                            onClick={() => setRoleChangeUser(orgUser)}
                          >
                            {orgUser.role}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyInvite(orgUser.email)}
                            title="Copy invite link"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveUser(orgUser.id)}
                            disabled={removeUserMutation.isPending}
                            title="Remove user"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Invite User Section */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <UserPlus className="h-4 w-4" />
                    <Label htmlFor="invite-email">Invite New User</Label>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="invite-email"
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="Enter email address..."
                      className="flex-1"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleInviteUser();
                        }
                      }}
                    />
                    <Button
                      onClick={handleInviteUser}
                      disabled={inviteUserMutation.isPending || !inviteEmail.trim()}
                    >
                      {inviteUserMutation.isPending ? (
                        "Inviting..."
                      ) : (
                        <>
                          <Mail className="h-4 w-4 mr-2" />
                          Invite
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter an email address to invite a user to this organization. If the user doesn't exist, a new account will be created.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditingOrg(null)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? "Updating..." : "Update"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {organizations?.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Organizations</h3>
            <p className="text-muted-foreground text-center mb-4">
              No organizations have been created yet. Create your first organization to get started.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Role Change Modal */}
      {roleChangeUser && (
        <UserRoleChangeModal
          isOpen={!!roleChangeUser}
          onClose={() => setRoleChangeUser(null)}
          onConfirm={handleRoleChange}
          currentRole={roleChangeUser.role}
          userName={formatUserName(roleChangeUser)}
          isLoading={updateUserRoleMutation.isPending}
        />
      )}
    </div>
  );
};

export default Organizations;
